import React, { useState, useMemo } from 'react';
import type { CSSProperties } from 'react';
import useStyles from '../../theme/useStyles';
import { useMemberDirectory, useFamilyDirectory } from '../../hooks';
import { formatDate } from '../../utils/format';
import {
  DataTable, FormLayout, Input, Select, Badge, Button, SearchBar
} from '../../components/ui';
import type { Notify } from '../../hooks/useNotification';
import type { AcademicYear, AcademicClass, SundayGrade, Teacher, Family, WeeklyRegister } from '../../types';
import type { SsRow } from './useSundaySchoolData';
import {
  ageOn, inAgeBand, ageBandsOverlap, nextSectionLetters, sectionLabel, spreadEvenly, groupSections,
  classAttendance, studentAttendance, LOW_ATTENDANCE_PCT
} from './registers';
import { TablePanel } from '../../components/ui';
import PrintButton from '../../components/ui/PrintButton';

// The Classes tab. Three levels, each a click deeper:
//
//   Grades            Grade 1 · age 5–6 · 3 sections · 41 students
//   └ Sections        A · 14 students · Teacher: Mary
//     └ Students      name, age, family, parent phone, attendance
//
// A grade is the level with an age band; a section is the room. Children are
// enrolled from the member directory — every member whose age at the year's
// start falls in the band is offered, ticked, and spread across the sections.
// The old flat classes (no grade) still show, each as a one-section grade of
// its own, until they are edited into a grade.

export interface EnrolledStudent {
  id?: string;
  name?: string;
  dob?: string;
  parentName?: string;
  phone?: string;
  classId?: string;
  memberId?: string;
  familyId?: string;
}

export interface Enrolment {
  name: string;
  dob: string;
  parentName: string;
  phone: string;
  classId: string;
  memberId: string;
  familyId: string;
}

export interface GradeBrowserProps {
  isDarkMode?: boolean;
  notify: Notify;
  years: SsRow<AcademicYear>[];
  /** The year every list is scoped to; '' means all. */
  yearScope: string;
  activeYearId?: string;
  grades: SsRow<SundayGrade>[];
  sections: SsRow<AcademicClass>[];
  teachers: SsRow<Teacher>[];
  students: EnrolledStudent[];
  registers: WeeklyRegister[];
  /** Several grades at once, each with how many sections to start with. */
  onCreateGrades: (drafts: Array<{ grade: Partial<SundayGrade>; sectionCount: number }>) => Promise<boolean>;
  onUpdateGrade: (rawId: number, grade: Partial<SundayGrade>) => Promise<boolean>;
  onVoidGrade: (grade: SsRow<SundayGrade>) => void;
  onAddSections: (grade: SsRow<SundayGrade>, letters: string[]) => Promise<boolean>;
  onUpdateSection: (rawId: number, section: Partial<AcademicClass>) => Promise<boolean>;
  onVoidSection: (section: SsRow<AcademicClass>) => void;
  onEnroll: (rows: Enrolment[]) => Promise<boolean>;
  onMoveStudent: (student: EnrolledStudent, classId: string) => Promise<boolean>;
  onRemoveStudent: (student: EnrolledStudent) => void;
  /** A non-member child for this section — opens the visitor form. */
  onAddVisitor: (sectionId: string) => void;
  /** Name, parent, phone, section — opens the student form prefilled. */
  onEditStudent: (student: EnrolledStudent) => void;
}

type View =
  | { level: 'grades' }
  | { level: 'sections'; gradeId: string }
  | { level: 'students'; gradeId: string; sectionId: string };

const EMPTY_GRADE = { name: '', academicYearId: '', ageFrom: '', ageTo: '', location: '' };
/** One line of the create form. */
interface GradeRow { name: string; ageFrom: string; ageTo: string; sectionCount: string; location: string }
const EMPTY_ROW: GradeRow = { name: '', ageFrom: '', ageTo: '', sectionCount: '1', location: '' };
const EMPTY_QUICK = { from: '1', to: '5', startAge: '5', span: '1', sections: '1' };

/** "Grade 3" -> "Grade 4"; a name with no trailing number gives nothing. */
const nextGradeName = (name?: string): string => {
  const m = /^(.*?)(\d+)\s*$/.exec(String(name || ''));
  return m ? `${m[1]}${Number(m[2]) + 1}` : '';
};
const SPREAD = '__spread__';

const GradeBrowser = ({
  isDarkMode, notify, years, yearScope, activeYearId, grades, sections, teachers, students, registers,
  onCreateGrades, onUpdateGrade, onVoidGrade, onAddSections, onUpdateSection, onVoidSection,
  onEnroll, onMoveStudent, onRemoveStudent, onAddVisitor, onEditStudent
}: GradeBrowserProps) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  const { members } = useMemberDirectory();
  const { families } = useFamilyDirectory();

  const [view, setView] = useState<View>({ level: 'grades' });
  const [gradeForm, setGradeForm] = useState(EMPTY_GRADE);
  const [gradeFormOpen, setGradeFormOpen] = useState(false);
  // The create form is a list: several grades are set up together at the
  // start of a year. `createYear` is shared by every row.
  const [createYear, setCreateYear] = useState('');
  const [rows, setRows] = useState<GradeRow[]>([{ ...EMPTY_ROW }]);
  const [quick, setQuick] = useState(EMPTY_QUICK);
  const [saving, setSaving] = useState(false);
  const [editingGrade, setEditingGrade] = useState<SsRow<SundayGrade> | null>(null);
  const [sectionForm, setSectionForm] = useState<{ rawId: number; section: string; location: string; gradeId: string } | null>(null);
  const [addCount, setAddCount] = useState('1');
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [enrollTarget, setEnrollTarget] = useState(SPREAD);
  const [enrollSearch, setEnrollSearch] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [moving, setMoving] = useState<Record<string, string>>({});

  const yearNameOf = (id) => years.find((y) => y.id === id)?.name || '-';
  const yearOf = (id) => years.find((y) => y.id === id);
  const familyOf = (id): Family | undefined => families.find((f) => f.familyId === id);
  const studentsIn = (sectionId) => students.filter((s) => s.classId === sectionId);
  const teachersIn = (sectionId) => teachers.filter((x) => x.classId === sectionId).map((x) => x.name).join(', ');

  const groups = useMemo(() => groupSections(grades, sections), [grades, sections]);
  const group = (gradeId) => groups.find((g) => g.grade.id === gradeId);
  const currentGroup = view.level === 'grades' ? undefined : group(view.gradeId);
  const currentGrade = currentGroup?.grade;
  const isSynthetic = Boolean(currentGrade && (currentGrade as { synthetic?: boolean }).synthetic);
  const gradeStudents = (g) => g.sections.flatMap((s) => studentsIn(s.id));

  // Age is taken at the start of the grade's year, so a birthday in the middle
  // of the year does not move a child between grades.
  const ageDateFor = (grade) => yearOf(grade?.academicYearId)?.startDate || new Date().toISOString().slice(0, 10);

  const overlapsWith = (grade) => grades.filter((g) =>
    g.id !== grade.id && g.academicYearId === grade.academicYearId && ageBandsOverlap(g, grade));

  // --- Grade form ---
  const openCreate = () => {
    setEditingGrade(null);
    setCreateYear(yearScope || activeYearId || '');
    setRows([{ ...EMPTY_ROW }]);
    setGradeFormOpen(true);
  };
  const setRow = (i: number, patch: Partial<GradeRow>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const removeRow = (i: number) =>
    setRows((prev) => (prev.length === 1 ? [{ ...EMPTY_ROW }] : prev.filter((_, j) => j !== i)));
  const addRow = () => setRows((prev) => {
    // Each new line picks up where the last one's age band ended.
    const last = prev[prev.length - 1];
    const lastTo = !last || last.ageTo === '' ? null : Number(last.ageTo);
    const span = last && last.ageFrom !== '' && lastTo !== null ? Math.max(0, lastTo - Number(last.ageFrom)) : 0;
    const nextFrom = lastTo === null || !Number.isFinite(lastTo) ? '' : String(lastTo + 1);
    return [...prev, {
      ...EMPTY_ROW,
      name: nextGradeName(last?.name),
      ageFrom: nextFrom,
      ageTo: nextFrom === '' ? '' : String(Number(nextFrom) + span),
      sectionCount: last?.sectionCount || '1',
      location: last?.location || ''
    }];
  });
  /** Fills the list with Grade N..M and consecutive age bands. */
  const quickFill = () => {
    const from = Number(quick.from);
    const to = Number(quick.to);
    const startAge = quick.startAge === '' ? null : Number(quick.startAge);
    const span = Math.max(1, Number(quick.span) || 1);
    if (!Number.isInteger(from) || !Number.isInteger(to) || to < from || to - from > 30) {
      notify('Give a sensible range, e.g. Grade 1 to 8.', 'error'); return;
    }
    const out: GradeRow[] = [];
    for (let g = from; g <= to; g += 1) {
      const age = startAge === null ? null : startAge + (g - from) * span;
      out.push({
        name: `Grade ${g}`,
        ageFrom: age === null ? '' : String(age),
        ageTo: age === null ? '' : String(age + span - 1),
        sectionCount: quick.sections || '1',
        location: ''
      });
    }
    setRows(out);
  };
  const openEdit = (g: SsRow<SundayGrade>) => {
    setEditingGrade(g);
    setGradeForm({
      name: g.name || '', academicYearId: g.academicYearId || '',
      ageFrom: String(g.ageFrom ?? ''), ageTo: String(g.ageTo ?? ''),
      location: g.location || ''
    });
    setGradeFormOpen(true);
  };
  const closeForm = () => { setGradeFormOpen(false); setEditingGrade(null); };

  /** Edit form: one grade. */
  const submitGrade = async (e) => {
    e.preventDefault();
    if (!editingGrade) return;
    const name = gradeForm.name.trim();
    if (!gradeForm.academicYearId) { notify('Pick the academic year.', 'error'); return; }
    if (!name) { notify('Grade name is required.', 'error'); return; }
    const from = gradeForm.ageFrom === '' ? null : Number(gradeForm.ageFrom);
    const to = gradeForm.ageTo === '' ? null : Number(gradeForm.ageTo);
    if (from !== null && to !== null && to < from) { notify('"Age to" must not be below "Age from".', 'error'); return; }
    const clash = grades.some((g) => g.rawId !== editingGrade.rawId
      && g.academicYearId === gradeForm.academicYearId
      && String(g.name || '').trim().toLowerCase() === name.toLowerCase());
    if (clash) { notify(`"${name}" already exists in ${yearNameOf(gradeForm.academicYearId)}.`, 'error'); return; }

    const payload = { name, academicYearId: gradeForm.academicYearId, ageFrom: from, ageTo: to, location: gradeForm.location.trim() };
    if (await onUpdateGrade(editingGrade.rawId as number, payload)) {
      notify('Grade updated.', 'success');
      closeForm();
    }
  };

  /** Create form: every filled row, checked together before anything is written. */
  const submitRows = async (e) => {
    e.preventDefault();
    if (!createYear) { notify('Pick the academic year.', 'error'); return; }
    const filled = rows.filter((r) => r.name.trim() || r.ageFrom || r.ageTo);
    if (filled.length === 0) { notify('Add at least one grade.', 'error'); return; }
    const seen = new Set<string>();
    const drafts: Array<{ grade: Partial<SundayGrade>; sectionCount: number }> = [];
    for (let i = 0; i < filled.length; i += 1) {
      const r = filled[i];
      const name = r.name.trim();
      if (!name) { notify(`Row ${rows.indexOf(r) + 1}: grade name is required.`, 'error'); return; }
      const key = name.toLowerCase();
      if (seen.has(key)) { notify(`"${name}" is listed twice.`, 'error'); return; }
      seen.add(key);
      if (grades.some((g) => g.academicYearId === createYear && String(g.name || '').trim().toLowerCase() === key)) {
        notify(`"${name}" already exists in ${yearNameOf(createYear)}.`, 'error'); return;
      }
      const from = r.ageFrom === '' ? null : Number(r.ageFrom);
      const to = r.ageTo === '' ? null : Number(r.ageTo);
      if ((from !== null && !Number.isFinite(from)) || (to !== null && !Number.isFinite(to))) { notify(`${name}: ages must be numbers.`, 'error'); return; }
      if (from !== null && to !== null && to < from) { notify(`${name}: "Age to" must not be below "Age from".`, 'error'); return; }
      const count = Number(r.sectionCount);
      if (!Number.isInteger(count) || count < 1 || count > 26) { notify(`${name}: sections must be between 1 and 26.`, 'error'); return; }
      drafts.push({ grade: { name, academicYearId: createYear, ageFrom: from, ageTo: to, location: r.location.trim() }, sectionCount: count });
    }
    setSaving(true);
    const ok = await onCreateGrades(drafts);
    setSaving(false);
    if (ok) {
      // Overlap is a warning, not a block: some parishes run a mixed class on purpose.
      const asBand = (g: { ageFrom?: unknown; ageTo?: unknown }, id: string) => ({ id, ageFrom: g.ageFrom, ageTo: g.ageTo });
      const all = [
        ...grades.filter((g) => g.academicYearId === createYear).map((g) => asBand(g, String(g.id))),
        ...drafts.map((d, i) => asBand(d.grade, `new-${i}`))
      ];
      const overlapping = drafts
        .filter((d, i) => all.some((g) => g.id !== `new-${i}` && ageBandsOverlap(g, asBand(d.grade, `new-${i}`))))
        .map((d) => d.grade.name);
      const sections = drafts.reduce((n, d) => n + d.sectionCount, 0);
      notify(`${drafts.length} grade${drafts.length === 1 ? '' : 's'} created with ${sections} section${sections === 1 ? '' : 's'}.${overlapping.length ? ` Note: age bands overlap for ${overlapping.join(', ')}.` : ''}`, 'success');
      closeForm();
    }
  };

  // --- Sections ---
  const addSections = async () => {
    if (!currentGrade || isSynthetic) return;
    const n = Number(addCount);
    if (!Number.isInteger(n) || n < 1) { notify('Enter how many sections to add.', 'error'); return; }
    const letters = nextSectionLetters(currentGroup.sections.map((s) => s.section || ''), n);
    if (await onAddSections(currentGrade as SsRow<SundayGrade>, letters)) {
      notify(`Added section${letters.length === 1 ? '' : 's'} ${letters.join(', ')}.`, 'success');
      setAddCount('1');
    }
  };

  const submitSection = async (e) => {
    e.preventDefault();
    if (!sectionForm) return;
    const grade = grades.find((g) => g.id === sectionForm.gradeId);
    const letter = sectionForm.section.trim().toUpperCase();
    if (grade && !letter) { notify('Section letter is required.', 'error'); return; }
    const taken = sections.some((s) => s.rawId !== sectionForm.rawId && s.gradeId === sectionForm.gradeId && grade
      && String(s.section || '').toUpperCase() === letter);
    if (taken) { notify(`Section ${letter} already exists in ${grade?.name}.`, 'error'); return; }
    const ok = await onUpdateSection(sectionForm.rawId, grade
      ? { gradeId: grade.id, section: letter, className: `${grade.name} – ${letter}`, academicYearId: grade.academicYearId, location: sectionForm.location }
      : { location: sectionForm.location });
    if (ok) {
      notify('Section updated.', 'success');
      setSectionForm(null);
      // A class that was just moved under a grade is no longer where we were looking.
      if (grade && isSynthetic) setView({ level: 'sections', gradeId: grade.id as string });
    }
  };

  // --- Enrolment from the directory ---
  const enrolledMemberIds = useMemo(() => new Set(students.map((s) => s.memberId).filter(Boolean)), [students]);
  const enrolledNameDob = useMemo(
    () => new Set(students.map((s) => `${String(s.name || '').trim().toLowerCase()}|${s.dob || ''}`)),
    [students]
  );

  const candidates = useMemo(() => {
    if (!currentGrade || isSynthetic) return [];
    const asOf = ageDateFor(currentGrade);
    return members
      .filter((m) => m.status !== 'Deceased' && m.status !== 'Transferred' && m.status !== 'Inactive')
      .map((m) => ({ member: m, age: ageOn(m.dateOfBirth, asOf) }))
      .filter(({ member, age }) => inAgeBand(age, currentGrade)
        && !enrolledMemberIds.has(member.memberId)
        // A child typed in by hand before the directory link existed is the
        // same child; do not offer them a second time.
        && !enrolledNameDob.has(`${String(member.name || '').trim().toLowerCase()}|${member.dateOfBirth || ''}`))
      .map(({ member, age }) => {
        const fam = familyOf(member.familyId);
        return {
          id: member.memberId as string,
          member,
          age,
          familyName: fam?.familyName || '-',
          parentName: fam?.headOfFamily || member.relativeName || '',
          phone: fam?.contactNumber || fam?.contact || member.mobileNumber || ''
        };
      })
      .sort((a, b) => (a.age ?? 0) - (b.age ?? 0) || String(a.member.name).localeCompare(String(b.member.name)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, families, currentGrade, isSynthetic, enrolledMemberIds, enrolledNameDob, years]);

  const visibleCandidates = useMemo(() => {
    const term = enrollSearch.trim().toLowerCase();
    if (!term) return candidates;
    return candidates.filter((c) => [c.member.name, c.familyName, c.parentName, c.phone, c.member.memberId]
      .some((v) => String(v || '').toLowerCase().includes(term)));
  }, [candidates, enrollSearch]);

  const toggleTick = (id: string) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const tickAllVisible = () => setTicked(new Set(visibleCandidates.map((c) => c.id)));

  const enroll = async () => {
    if (!currentGroup || currentGroup.sections.length === 0) { notify('Add a section first.', 'error'); return; }
    const chosen = candidates.filter((c) => ticked.has(c.id));
    if (chosen.length === 0) { notify('Tick at least one child to enrol.', 'error'); return; }
    const counts: Record<string, number> = {};
    currentGroup.sections.forEach((s) => { counts[s.id as string] = studentsIn(s.id).length; });
    const placement = enrollTarget === SPREAD
      ? spreadEvenly(chosen.map((c) => c.id), currentGroup.sections, counts)
      : Object.fromEntries(chosen.map((c) => [c.id, enrollTarget]));
    setEnrolling(true);
    const ok = await onEnroll(chosen.map((c) => ({
      name: c.member.name || '',
      dob: c.member.dateOfBirth || '',
      parentName: c.parentName,
      phone: c.phone,
      classId: placement[c.id],
      memberId: c.member.memberId || '',
      familyId: c.member.familyId || ''
    })));
    setEnrolling(false);
    if (ok) {
      notify(`${chosen.length} student${chosen.length === 1 ? '' : 's'} enrolled.`, 'success');
      setTicked(new Set());
      setEnrollOpen(false);
    }
  };

  // --- Styles ---
  const card: CSSProperties = {
    backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`, borderRadius: t.radius.lg,
    padding: t.spacing.md, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '6px'
  };
  const grid: CSSProperties = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: t.spacing.md, marginBottom: t.spacing.lg
  };
  const sub: CSSProperties = { fontSize: '12px', color: t.textFaint };
  const crumb: CSSProperties = { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: t.spacing.md, flexWrap: 'wrap' };
  const link: CSSProperties = { color: t.info, cursor: 'pointer', fontWeight: 'bold' };
  const ageBand = (g) => (g.ageFrom == null && g.ageTo == null) || (g.ageFrom === '' && g.ageTo === '')
    ? 'Any age'
    : `Age ${g.ageFrom ?? '…'}–${g.ageTo ?? '…'}`;
  const pctBadge = (pct: number | null) => pct === null
    ? <span style={sub}>no register yet</span>
    : <Badge tone={pct >= 80 ? 'success' : pct >= LOW_ATTENDANCE_PCT ? 'warning' : 'error'}>{pct.toFixed(0)}% attendance</Badge>;

  const breadcrumb = (
    <div style={crumb}>
      <span style={view.level === 'grades' ? { fontWeight: 'bold', color: t.textMain } : link} onClick={() => setView({ level: 'grades' })}>Grades</span>
      {view.level !== 'grades' && currentGrade && (
        <>
          <span style={sub}>›</span>
          <span style={view.level === 'sections' ? { fontWeight: 'bold', color: t.textMain } : link}
            onClick={() => setView({ level: 'sections', gradeId: view.gradeId })}>{currentGrade.name}</span>
        </>
      )}
      {view.level === 'students' && (
        <>
          <span style={sub}>›</span>
          <span style={{ fontWeight: 'bold', color: t.textMain }}>
            {(() => { const s = sections.find((x) => x.id === view.sectionId); return s ? (s.section ? `Section ${s.section}` : s.className) : ''; })()}
          </span>
        </>
      )}
    </div>
  );

  const rowInput: CSSProperties = { margin: 0 };
  const filledRows = rows.filter((r) => r.name.trim()).length;
  const gradeFormPanel = gradeFormOpen && (editingGrade ? (
    <FormLayout
      title={`Edit ${editingGrade.name}`}
      submitLabel="Update Grade"
      onSubmit={submitGrade}
      onCancel={closeForm}
      maxWidth="100%"
    >
      <FormLayout.Grid columns={5}>
        <Select label="Academic Year" required
          options={years.map((y) => ({ value: y.id, label: `${y.name}${y.id === activeYearId ? ' (current)' : ''}` }))}
          value={gradeForm.academicYearId}
          onChange={(e) => setGradeForm({ ...gradeForm, academicYearId: e.target.value })} />
        <Input label="Grade Name" required placeholder="e.g. Grade 1"
          value={gradeForm.name} onChange={(e) => setGradeForm({ ...gradeForm, name: e.target.value })} />
        <Input label="Age from" type="number" min="0" placeholder="e.g. 5"
          hint="At the year's start date."
          value={gradeForm.ageFrom} onChange={(e) => setGradeForm({ ...gradeForm, ageFrom: e.target.value })} />
        <Input label="Age to" type="number" min="0" placeholder="e.g. 6"
          value={gradeForm.ageTo} onChange={(e) => setGradeForm({ ...gradeForm, ageTo: e.target.value })} />
        <Input label="Location" placeholder="e.g. Parish Hall"
          value={gradeForm.location} onChange={(e) => setGradeForm({ ...gradeForm, location: e.target.value })} />
      </FormLayout.Grid>
    </FormLayout>
  ) : (
    <FormLayout
      title="Create Grades"
      submitLabel={saving ? 'Creating…' : `Create ${filledRows || ''} Grade${filledRows === 1 ? '' : 's'} & Sections`}
      submitting={saving}
      onSubmit={submitRows}
      onCancel={closeForm}
      maxWidth="100%"
    >
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: t.spacing.md }}>
        <Select label="Academic Year (for every row)" required containerStyle={{ ...rowInput, minWidth: '220px' }}
          options={years.map((y) => ({ value: y.id, label: `${y.name}${y.id === activeYearId ? ' (current)' : ''}` }))}
          value={createYear} onChange={(e) => setCreateYear(e.target.value)} />
        {/* Quick fill: the usual shape is Grade 1..N with consecutive age bands. */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '8px', border: `1px dashed ${t.border}`, borderRadius: t.radius.lg }}>
          <span style={{ ...sub, alignSelf: 'center' }}>Quick fill:</span>
          <Input label="Grade from" type="number" min="0" containerStyle={{ ...rowInput, width: '90px' }} value={quick.from} onChange={(e) => setQuick({ ...quick, from: e.target.value })} />
          <Input label="to" type="number" min="0" containerStyle={{ ...rowInput, width: '80px' }} value={quick.to} onChange={(e) => setQuick({ ...quick, to: e.target.value })} />
          <Input label="Start age" type="number" min="0" containerStyle={{ ...rowInput, width: '90px' }} value={quick.startAge} onChange={(e) => setQuick({ ...quick, startAge: e.target.value })} />
          <Input label="Years / grade" type="number" min="1" containerStyle={{ ...rowInput, width: '100px' }} value={quick.span} onChange={(e) => setQuick({ ...quick, span: e.target.value })} />
          <Input label="Sections" type="number" min="1" max="26" containerStyle={{ ...rowInput, width: '90px' }} value={quick.sections} onChange={(e) => setQuick({ ...quick, sections: e.target.value })} />
          <Button type="button" variant="secondary" size="sm" onClick={quickFill}>Fill list</Button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
          <thead>
            <tr style={{ ...sub, textAlign: 'left' }}>
              <th style={{ padding: '4px 6px', width: '32px' }}>#</th>
              <th style={{ padding: '4px 6px' }}>Grade Name</th>
              <th style={{ padding: '4px 6px', width: '110px' }}>Age from</th>
              <th style={{ padding: '4px 6px', width: '110px' }}>Age to</th>
              <th style={{ padding: '4px 6px', width: '120px' }}>Sections</th>
              <th style={{ padding: '4px 6px' }}>Location</th>
              <th style={{ padding: '4px 6px', width: '60px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${t.borderDashed}`, verticalAlign: 'top' }}>
                <td style={{ padding: '8px 6px', color: t.textFaint }}>{i + 1}</td>
                <td style={{ padding: '4px 6px' }}>
                  <Input placeholder={`e.g. Grade ${i + 1}`} containerStyle={rowInput} value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <Input type="number" min="0" placeholder="5" containerStyle={rowInput} value={r.ageFrom} onChange={(e) => setRow(i, { ageFrom: e.target.value })} />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <Input type="number" min="0" placeholder="6" containerStyle={rowInput} value={r.ageTo} onChange={(e) => setRow(i, { ageTo: e.target.value })} />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <Input type="number" min="1" max="26" step="1" containerStyle={rowInput}
                    hint={nextSectionLetters([], Math.max(1, Math.min(26, Number(r.sectionCount) || 1))).join(', ')}
                    value={r.sectionCount} onChange={(e) => setRow(i, { sectionCount: e.target.value })} />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <Input placeholder="optional" containerStyle={rowInput} value={r.location} onChange={(e) => setRow(i, { location: e.target.value })} />
                </td>
                <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                  <Button type="button" variant="ghost" size="sm" title="Remove row" onClick={() => removeRow(i)}>✕</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', gap: '8px', flexWrap: 'wrap' }}>
        <Button type="button" variant="secondary" size="sm" onClick={addRow}>+ Add another grade</Button>
        <span style={sub}>Ages are measured at the year's start date. Sections are lettered A, B, C… automatically.</span>
      </div>
    </FormLayout>
  ));

  // ---------------------------------------------------------------- Level 1
  if (view.level === 'grades') {
    const visible = groups.filter((g) => !yearScope || g.grade.academicYearId === yearScope);
    return (
      <>
        {breadcrumb}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: t.spacing.md, gap: '8px', flexWrap: 'wrap' }}>
          <div style={styles.sectionTitle}>Grades — {yearScope ? yearNameOf(yearScope) : 'All years'}</div>
          {!gradeFormOpen && <Button write onClick={openCreate} disabled={years.length === 0}>+ Create Grade</Button>}
        </div>
        {gradeFormPanel}
        {visible.length === 0 ? (
          <div style={{ ...card, cursor: 'default', color: t.textFaint, fontStyle: 'italic' }}>
            No grades yet. Create one — e.g. "Grade 1", age 5–6, 3 sections.
          </div>
        ) : (
          <div style={grid}>
            {visible.map((g) => {
              const kids = gradeStudents(g);
              const overlap = (g.grade as { synthetic?: boolean }).synthetic ? [] : overlapsWith(g.grade);
              const synthetic = (g.grade as { synthetic?: boolean }).synthetic;
              return (
                <div key={g.grade.id} style={{ ...card, borderLeft: `4px solid ${synthetic ? t.warning : t.info}` }}
                  onClick={() => setView({ level: 'sections', gradeId: g.grade.id as string })}>
                  <div style={{ fontSize: t.fontSize.lg, fontWeight: 900, color: t.textMain }}>🎓 {g.grade.name}</div>
                  <div style={sub}>{synthetic ? 'Not yet in a grade' : ageBand(g.grade)} · {yearNameOf(g.grade.academicYearId)}</div>
                  <div style={{ color: t.textMain }}>
                    <strong>{g.sections.length}</strong> section{g.sections.length === 1 ? '' : 's'} · <strong>{kids.length}</strong> students
                  </div>
                  <div style={sub}>{g.sections.map((s) => s.section || s.className).join(' · ')}</div>
                  {overlap.length > 0 && <div style={{ ...sub, color: t.warning }}>⚠️ Age band overlaps {overlap.map((o) => o.name).join(', ')}</div>}
                  {!synthetic && (
                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                      <Button write variant="ghost" size="sm" onClick={() => openEdit(g.grade as SsRow<SundayGrade>)}>Edit</Button>
                      <Button variant="danger" size="sm" style={styles.deleteBtn} disabled={kids.length > 0}
                        title={kids.length > 0 ? 'Move or remove its students first' : undefined}
                        onClick={() => onVoidGrade(g.grade as SsRow<SundayGrade>)}>Delete</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  if (!currentGroup || !currentGrade) {
    return <>{breadcrumb}<div style={sub}>That grade is no longer here.</div></>;
  }

  // ---------------------------------------------------------------- Level 2
  if (view.level === 'sections') {
    const kids = gradeStudents(currentGroup);
    return (
      <>
        {breadcrumb}
        <div style={{ ...card, cursor: 'default', marginBottom: t.spacing.lg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: t.fontSize.lg, fontWeight: 900, color: t.textMain }}>🎓 {currentGrade.name}</div>
              <div style={sub}>
                {isSynthetic ? 'A class saved before grades existed — edit the section below to place it in a grade.' : ageBand(currentGrade)}
                {' · '}{yearNameOf(currentGrade.academicYearId)} · {kids.length} students · {currentGroup.sections.length} sections
              </div>
            </div>
            {!isSynthetic && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <Button write onClick={() => { setEnrollOpen((v) => !v); setTicked(new Set()); }}>
                  👨‍👩‍👧 Enroll from Members{candidates.length ? ` (${candidates.length})` : ''}
                </Button>
                <Button write variant="ghost" onClick={() => openEdit(currentGrade as SsRow<SundayGrade>)}>Edit Grade</Button>
              </div>
            )}
          </div>
        </div>
        {gradeFormPanel}

        {enrollOpen && !isSynthetic && (
          <div style={{ ...card, cursor: 'default', marginBottom: t.spacing.lg, borderLeft: `4px solid ${t.income}` }}>
            <div style={styles.sectionTitle}>
              Members aged {ageBand(currentGrade).replace('Age ', '')} as of {formatDate(ageDateFor(currentGrade))} — not yet enrolled
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <SearchBar value={enrollSearch} onChange={setEnrollSearch} onClear={() => setEnrollSearch('')}
                placeholder="Search name, family, parent or phone..." fieldStyle={{ flex: '1 1 260px' }} />
              <Select label="Put them in" placeholder="" containerStyle={{ margin: 0, minWidth: '200px' }}
                value={enrollTarget} onChange={(e) => setEnrollTarget(e.target.value)}
                options={[
                  { value: SPREAD, label: 'Spread evenly across sections' },
                  ...currentGroup.sections.map((s) => ({ value: s.id as string, label: `Section ${s.section || s.className} (${studentsIn(s.id).length} now)` }))
                ]} />
            </div>
            <DataTable
              minWidth="640px"
              columns={[
                {
                  key: 'tick', header: <input type="checkbox" checked={visibleCandidates.length > 0 && visibleCandidates.every((c) => ticked.has(c.id))} onChange={(e) => (e.target.checked ? tickAllVisible() : setTicked(new Set()))} />,
                  align: 'center',
                  render: (c) => <input type="checkbox" checked={ticked.has(c.id)} onChange={() => toggleTick(c.id)} />
                },
                { key: 'name', header: 'Child', render: (c) => <><strong>{c.member.name}</strong><br /><span style={sub}>{c.member.memberId}</span></> },
                { key: 'age', header: 'Age', align: 'center', render: (c) => c.age ?? '-' },
                { key: 'familyName', header: 'Family' },
                { key: 'parentName', header: 'Parent / Guardian', render: (c) => <>{c.parentName || '-'}<br /><span style={sub}>{c.phone}</span></> }
              ]}
              rows={visibleCandidates}
              rowKey="id"
              emptyMessage={candidates.length === 0 ? 'Every member in this age band is already enrolled.' : 'No match.'}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '8px' }}>
              <Button variant="secondary" onClick={() => setEnrollOpen(false)}>Close</Button>
              <Button write onClick={enroll} disabled={enrolling || ticked.size === 0}>
                {enrolling ? 'Enrolling…' : `Enroll ${ticked.size} selected`}
              </Button>
            </div>
          </div>
        )}

        {sectionForm && (
          <FormLayout title="Edit Section" submitLabel="Update Section" onSubmit={submitSection} onCancel={() => setSectionForm(null)} maxWidth="100%">
            <FormLayout.Grid columns={3}>
              <Select label="Grade" placeholder={isSynthetic ? '— Keep as a standalone class —' : ''}
                options={grades.filter((g) => !yearScope || g.academicYearId === yearScope).map((g) => ({ value: g.id as string, label: `${g.name} (${yearNameOf(g.academicYearId)})` }))}
                value={sectionForm.gradeId} onChange={(e) => setSectionForm({ ...sectionForm, gradeId: e.target.value })} />
              <Input label="Section letter" placeholder="A" disabled={!sectionForm.gradeId}
                value={sectionForm.section} onChange={(e) => setSectionForm({ ...sectionForm, section: e.target.value.toUpperCase() })} />
              <Input label="Location" value={sectionForm.location} onChange={(e) => setSectionForm({ ...sectionForm, location: e.target.value })} />
            </FormLayout.Grid>
          </FormLayout>
        )}

        <div style={grid}>
          {currentGroup.sections.map((s) => {
            const n = studentsIn(s.id).length;
            return (
              <div key={s.id} style={{ ...card, borderLeft: `4px solid ${t.income}` }}
                onClick={() => setView({ level: 'students', gradeId: view.gradeId, sectionId: s.id as string })}>
                <div style={{ fontSize: t.fontSize.lg, fontWeight: 900, color: t.textMain }}>
                  {s.section ? `Section ${s.section}` : s.className}
                </div>
                <div style={{ color: t.textMain }}><strong>{n}</strong> student{n === 1 ? '' : 's'}</div>
                <div style={sub}>Teacher: {teachersIn(s.id) || <em>none assigned</em>}</div>
                <div>{pctBadge(classAttendance(registers, s.id as string).pct)}</div>
                {s.location && <div style={sub}>📍 {s.location}</div>}
                <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                  <Button write variant="ghost" size="sm" onClick={() => setSectionForm({ rawId: s.rawId as number, section: s.section || '', location: s.location || '', gradeId: s.gradeId || '' })}>Edit</Button>
                  <Button variant="danger" size="sm" style={styles.deleteBtn} disabled={n > 0}
                    title={n > 0 ? 'Move or remove its students first' : undefined}
                    onClick={() => onVoidSection(s)}>Delete</Button>
                </div>
              </div>
            );
          })}
          {!isSynthetic && (
            <div style={{ ...card, cursor: 'default', borderStyle: 'dashed', justifyContent: 'center' }}>
              <div style={{ fontWeight: 'bold', color: t.textMain }}>+ Add sections</div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                <Input label="How many" type="number" min="1" step="1" containerStyle={{ margin: 0, flex: 1 }}
                  value={addCount} onChange={(e) => setAddCount(e.target.value)} />
                <Button write onClick={addSections}>Add</Button>
              </div>
              <div style={sub}>Next: {nextSectionLetters(currentGroup.sections.map((x) => x.section || ''), Math.max(1, Number(addCount) || 1)).join(', ')}</div>
            </div>
          )}
        </div>
      </>
    );
  }

  // ---------------------------------------------------------------- Level 3
  const section = sections.find((s) => s.id === view.sectionId);
  if (!section) return <>{breadcrumb}<div style={sub}>That section is no longer here.</div></>;
  const roll = studentsIn(section.id);
  const siblings = currentGroup.sections.filter((s) => s.id !== section.id);
  const asOf = ageDateFor(currentGrade);

  return (
    <>
      {breadcrumb}
      <div style={{ ...card, cursor: 'default', marginBottom: t.spacing.lg }}>
        <div style={{ fontSize: t.fontSize.lg, fontWeight: 900, color: t.textMain }}>
          {sectionLabel(section, isSynthetic ? null : currentGrade)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={sub}>
            {roll.length} students · Teacher: {teachersIn(section.id) || 'none assigned'} · {pctBadge(classAttendance(registers, section.id as string).pct)}
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '6px' }}>
            {!isSynthetic && <Button write size="sm" onClick={() => { setView({ level: 'sections', gradeId: view.gradeId }); setEnrollOpen(true); setTicked(new Set()); }}>👨‍👩‍👧 Enroll from Members</Button>}
            <Button write size="sm" variant="secondary" onClick={() => onAddVisitor(section.id as string)}>+ Add visitor child</Button>
            <PrintButton
              module="Sunday School"
              documentType="SUNDAY_SCHOOL_ROSTER"
              size="sm"
              title="Class-Register"
              data={() => ({
                title: 'Sunday School Class Register',
                className: sectionLabel(section, isSynthetic ? null : currentGrade),
                gradeName: isSynthetic ? '' : (currentGrade?.name || ''),
                teacher: teachersIn(section.id) || 'None assigned',
                reference: sectionLabel(section, isSynthetic ? null : currentGrade),
                rowCount: roll.length,
                // Age and attendance are worked out for the screen; the sheet
                // carries the same figures rather than the raw dates.
                rows: roll.map((st) => {
                  const att = studentAttendance(registers, st.id as string);
                  return {
                    studentId: st.id || '',
                    name: st.name || '',
                    memberId: st.memberId || '',
                    age: ageOn(st.dob, asOf) ?? '',
                    familyName: familyOf(st.familyId)?.familyName || '',
                    parentName: st.parentName || '',
                    phone: st.phone || '',
                    attendance: att.pct === null ? '' : att.pct.toFixed(0) + '%'
                  };
                })
              })}
              onNotify={notify}
              isDarkMode={isDarkMode}
            />
          </span>
        </div>
      </div>
      <TablePanel
        title="Students"
           isDarkMode={isDarkMode}
        minWidth="760px"
        columns={[
          { key: 'name', header: 'Student', render: (s) => <><strong>{s.name}</strong><br /><span style={sub}>{s.id}{s.memberId ? ` · Member ${s.memberId}` : ' · not linked to a member'}</span></> },
          { key: 'age', header: 'Age', align: 'center', render: (s) => ageOn(s.dob, asOf) ?? '-' },
          { key: 'family', header: 'Family', render: (s) => familyOf(s.familyId)?.familyName || '-' },
          { key: 'parent', header: 'Parent / Guardian', render: (s) => <>{s.parentName || '-'}<br /><span style={sub}>{s.phone || ''}</span></> },
          {
            key: 'attendance', header: 'Attendance', align: 'center',
            render: (s) => { const r = studentAttendance(registers, s.id as string); return r.pct === null ? <span style={sub}>—</span> : <Badge tone={r.pct >= 80 ? 'success' : r.pct >= LOW_ATTENDANCE_PCT ? 'warning' : 'error'}>{r.pct.toFixed(0)}%</Badge>; }
          },
          {
            key: 'actions', header: 'Action', align: 'right',
            render: (s) => (
              <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end', alignItems: 'center' }}>
                {siblings.length > 0 && (
                  <Select placeholder="Move to…" containerStyle={{ margin: 0, minWidth: '130px' }}
                    value={moving[s.id as string] || ''}
                    onChange={async (e) => {
                      const target = e.target.value;
                      if (!target) return;
                      setMoving((m) => ({ ...m, [s.id as string]: target }));
                      if (await onMoveStudent(s, target)) notify(`${s.name} moved.`, 'success');
                      setMoving((m) => ({ ...m, [s.id as string]: '' }));
                    }}
                    options={siblings.map((x) => ({ value: x.id as string, label: x.section ? `Section ${x.section}` : x.className }))} />
                )}
                <Button write variant="ghost" size="sm" onClick={() => onEditStudent(s)}>Edit</Button>
                <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => onRemoveStudent(s)}>Remove</Button>
              </div>
            )
          }
        ]}
        rows={roll}
        rowKey="id"
        emptyMessage="No students in this section yet. Use “Enroll from Members”, or add a visitor child."
      />
    </>
  );
};

export default GradeBrowser;
